import userStore from "../store/session-user-store"
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";

const API_SECRET = 'presta-slot-api-secret-key';
const API_URL = process.env.REACT_APP_URL_SERVER;

export enum HTTP_STATUS_CODE {
	OK = 200,
	CREATED = 201,
	NO_CONTENT = 204,
	BAD_REQUEST = 400,
	UNAUTHORIZED = 401,
	FORBIDDEN = 403,
	NOT_FOUND = 404,
	METHOD_NOT_ALLOWED = 405,
	UNPROCESSABLE_ENTITY = 422,
	INTERNAL_SERVER_ERROR = 500,
}

// Variable pour suivre si un refresh est déjà en cours
let isRefreshing = false;
// File d'attente pour les requêtes en attente pendant le refresh
let refreshSubscribers: Array<(token: string) => void> = [];

// Fonction pour ajouter les requêtes à la file d'attente
const subscribeTokenRefresh = (callback: (token: string) => void) => {
	refreshSubscribers.push(callback);
};

// Fonction pour exécuter toutes les requêtes en attente une fois le token rafraîchi
const onTokenRefreshed = (token: string) => {
	refreshSubscribers.forEach(callback => callback(token));
	refreshSubscribers = [];
};

// Fonction pour rafraîchir le token
const refreshToken = async (): Promise<any> => {
	try {
		const response = await fetch(`${API_URL}/public-api/auth/refresh-token`, {
			method: 'POST',
			body: JSON.stringify({ refreshTokenUserAuth: userStore.getRefreshToken() }),
			headers: UHttp.publicApiHeaders()
		});

		if (!response.ok) {
			Cookies.remove("MODESSID");
			Cookies.remove("MODESSID_REFRESH");
			const pathParts = window.location.pathname.split('/');
			const currentLang = pathParts.length > 1 && pathParts[1].length === 2 ? pathParts[1] : 'fr';
			window.location.href = `/${currentLang}/login`;
			return null;
		}

		const data = await response.json();
		userStore.setToken(data.newToken);

		return data.newToken;
	} catch (error) {
		Cookies.remove("MODESSID");
		Cookies.remove("MODESSID_REFRESH");
		// Rediriger vers la page de login avec la langue actuelle
		const pathParts = window.location.pathname.split('/');
		const currentLang = pathParts.length > 1 && pathParts[1].length === 2 ? pathParts[1] : 'fr';
		window.location.href = `/${currentLang}/login`;
		throw error;
	}
};

export class UHttp {

	public static getCurrentHour = (): number => Math.floor(Date.now() / 3600000);

	public static generateTokenForHour = (hourTimestamp: number): string => {
		const hmac = CryptoJS.HmacSHA256(hourTimestamp.toString(), API_SECRET);
		return hmac.toString(CryptoJS.enc.Hex);
	};

	public static getCurrentApiToken = (): string => this.generateTokenForHour(this.getCurrentHour());


	private static async fetchWithInterceptor(
		url: string,
		options: RequestInit,
		retryCount = 0
	): Promise<Response | any> {

		try {

			const response = await fetch(url, options);

			// Si la réponse est 401 (Unauthorized) et nous n'avons pas déjà essayé de rafraîchir
			if (response.status === HTTP_STATUS_CODE.UNAUTHORIZED && retryCount === 0) {
				// Si un refresh est déjà en cours, mettre cette requête en attente
				if (isRefreshing) {
					return new Promise((resolve) => {
						subscribeTokenRefresh((token: string) => {
							options.headers = {
								...options.headers,
								'Authorization': `Bearer ${token}`
							};
							// Réessayer la requête avec le nouveau token
							resolve(this.fetchWithInterceptor(url, options, retryCount + 1));
						});
					});
				}

				// Sinon, démarrer un processus de refresh
				isRefreshing = true;

				try {
					const newToken = await refreshToken();
					isRefreshing = false;

					// Notifier toutes les requêtes en attente
					onTokenRefreshed(newToken);

					// Mettre à jour les headers et réessayer
					options.headers = {
						...options.headers,
						'Authorization': `Bearer ${newToken}`
					};

					return this.fetchWithInterceptor(url, options, retryCount + 1);
				} catch (error) {
					isRefreshing = false;
					// Rediriger vers la page de login avec la langue actuelle
					const pathParts = window.location.pathname.split('/');
					const currentLang = pathParts.length > 1 && pathParts[1].length === 2 ? pathParts[1] : 'fr';
					window.location.href = `/${currentLang}/login`;
					throw error;
				}
			}

			return response;
		} catch (error) {
			console.error('Fetch error:', error);
			throw error;
		}
	}

	public static async post(url: string, data: any, headers: any = {}): Promise<Response> {
		return this.fetchWithInterceptor(url, {
			method: "POST",
			body: JSON.stringify(data),
			headers
		});
	}

	public static async postFormData(url: string, formData: FormData, headers: any = {}): Promise<Response> {
		// Pour FormData, on ne doit pas inclure Content-Type car le navigateur le définit automatiquement avec la boundary
		const headersWithoutContentType = { ...headers };
		delete headersWithoutContentType['Content-Type'];

		return this.fetchWithInterceptor(url, {
			method: "POST",
			body: formData,
			headers: headersWithoutContentType
		});
	}

	public static async get(url: string, headers: any = {}): Promise<Response> {
		return this.fetchWithInterceptor(url, {
			method: "GET",
			headers
		});
	}

	public static async put(url: string, data: any, headers: any = {}): Promise<Response> {
		return this.fetchWithInterceptor(url, {
			method: "PUT",
			body: JSON.stringify(data),
			headers
		});
	}

	public static async putFormData(url: string, formData: FormData, headers: any = {}): Promise<Response> {
		const headersWithoutContentType = { ...headers };
		delete headersWithoutContentType['Content-Type'];

		return this.fetchWithInterceptor(url, {
			method: "PUT",
			body: formData,
			headers: headersWithoutContentType
		});
	}

	public static async patch(url: string, data: any, headers: any = {}): Promise<Response> {
		return this.fetchWithInterceptor(url, {
			method: "PATCH",
			body: JSON.stringify(data),
			headers
		});
	}

	public static async delete(url: string, headers: any = {}): Promise<Response> {
		return this.fetchWithInterceptor(url, {
			method: "DELETE",
			headers
		});
	}

	public static defaultHeaders(): { Accept: string; "Content-Type": string } {
		return {
			'Accept': 'application/json, text/plain, */*',
			'Content-Type': 'application/json'
		}
	}

	public static authHeaders(): { Authorization: string; Accept: string; "Content-Type": string } {
		return {
			...this.defaultHeaders(),
			'Authorization': "Bearer " + userStore.getToken()
		}
	}

	public static publicApiHeaders = () => ({
		...this.defaultHeaders(),
		'x-api-token': `Bearer ${this.getCurrentApiToken()}`
	});

	public static getLastUrlSegment(url: string): string {
		const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;

		const segments = cleanUrl.split('/');
		return segments[segments.length - 1];
	}
}
